// Multivariate Dependencies
import React, { useState, useMemo, useEffect } from 'react';
import ReactDOM from 'react-dom';
// Components
import { TooltipContainer } from './styles/TooltipContainer';
import { SuspenseWrapper } from '../../common/components/SuspenseWrapper';
import { DirectionalContainer } from './components/DirectionalContainer';
// Styles
import { boxShadows } from '../../common/styles/boxShadows';
// Utils
import { setTooltipSettingsDebounced } from './utils/setTooltipSettingsDebounced';
import { openTooltipEventHandler } from './utils/openTooltipEventHandler';
import { closeTooltipEventHandler } from './utils/closeTooltipEventHandler';
import { TooltipSyntheticEventNames } from './types/TooltipSyntheticEventNames';
import { DirectionalTooltipStylesByTooltipDirection } from './types/DirectionalTooltipStylesByTooltipDirection';
import { DefaultTooltipDirectionStyles } from './types/DefaultTooltipDirectionStyles';
import { ZIndex } from '../../common/types/ZIndex';
// Hooks
import { useTheme } from 'styled-components';
/**
 * @deprecated This component has been replaced with src/molecules/Tooltip (with an uppercase). This component will remain here until
 * it's usage in dropdown has been replaced
 *
 * @description A generic tooltip component that utilises synthetic events to open/close tooltips.
 * Ensure that that the publishedTooltipEventElement that is passed in is also utilised on
 * the component that intends to interact with tooltips (publishing/subscribing on the same element)
 *
 * To utilise directional tooltips, this tooltip component must be wrapped in a container with position relative.
 * This container will then act as an anchor
 *
 * @example
 *  const Example = (props: any) => {
      const [tooltipEventElement, setTooltipEventElement] = React.useState<HTMLDivElement>();
      const tooltipEventCallback = useCallback((ref: HTMLDivElement) => {
        ref && setTooltipEventElement(ref);
      }, []);

      // The div that represents the 'tooltipEventElement' does not need to be a parent, it can also be a sibling
      return (
        <div ref={tooltipEventCallback} style={{ position: 'relative' }}> // Position is set to relative if we want directional tooltips
          <Tooltip showTooltipDelay={400} publishedTooltipEventElement={tooltipEventElement} />
          <CustomComponent subscribedTooltipEventElement={tooltipEventElement} />
        </div>
      );
    };
 * @template C Type of the data that is provided to the user when constructing the tooltip content
 * @param {TooltipProps} { delay, publishedTooltipEventElement }
 * @return {*}
 */
export var Tooltip = function (_a) {
    var showTooltipDelay = _a.showTooltipDelay, publishedTooltipEventElement = _a.publishedTooltipEventElement, directionToOpen = _a.directionToOpen, _b = _a.showDirectionalArrow, showDirectionalArrow = _b === void 0 ? true : _b;
    var themeStyles = useTheme();
    var _c = useState({
        x: 0,
        y: 0,
        content: null,
        isVisible: false,
    }), tooltipSettings = _c[0], setTooltipSettings = _c[1];
    var setTooltipSettingsWithDelay = useMemo(function () { return setTooltipSettingsDebounced(showTooltipDelay); }, [showTooltipDelay]);
    // Add event listeners to given element and remove the events when this component is removed
    useEffect(function () {
        var openTooltipEvent = openTooltipEventHandler(setTooltipSettingsWithDelay, setTooltipSettings);
        var closeTooltipEvent = closeTooltipEventHandler(setTooltipSettingsWithDelay, setTooltipSettings);
        publishedTooltipEventElement === null || publishedTooltipEventElement === void 0 ? void 0 : publishedTooltipEventElement.addEventListener(TooltipSyntheticEventNames.Open, openTooltipEvent);
        publishedTooltipEventElement === null || publishedTooltipEventElement === void 0 ? void 0 : publishedTooltipEventElement.addEventListener(TooltipSyntheticEventNames.Close, closeTooltipEvent);
        return function () {
            publishedTooltipEventElement === null || publishedTooltipEventElement === void 0 ? void 0 : publishedTooltipEventElement.removeEventListener(TooltipSyntheticEventNames.Open, openTooltipEvent);
            publishedTooltipEventElement === null || publishedTooltipEventElement === void 0 ? void 0 : publishedTooltipEventElement.removeEventListener(TooltipSyntheticEventNames.Close, closeTooltipEvent);
        };
    }, [publishedTooltipEventElement, setTooltipSettingsWithDelay]);
    var _d = directionToOpen
        ? DirectionalTooltipStylesByTooltipDirection[directionToOpen]
        : DefaultTooltipDirectionStyles, directionalContainerStyles = _d.directionalContainerStyles, tooltipContainerStyles = _d.tooltipContainerStyles, tooltipArrowStyles = _d.tooltipArrowStyles;
    return (tooltipSettings.isVisible &&
        ReactDOM.createPortal(React.createElement(DirectionalContainer, { directionalContainerStyles: directionToOpen && directionalContainerStyles },
            React.createElement(TooltipContainer, { position: directionToOpen ? 'absolute' : 'fixed', width: 'fit-content', height: 'fit-content', padding: '12px', borderRadius: '4px', boxShadow: "0px 2px 13px " + boxShadows[300], backgroundColor: themeStyles.backgroundUI, zIndex: ZIndex.TOOLTIP + ZIndex.PORTAL, top: directionToOpen ? tooltipContainerStyles.top(showDirectionalArrow) : tooltipSettings.y + "px", bottom: directionToOpen ? tooltipContainerStyles.bottom(showDirectionalArrow) : null, left: directionToOpen ? tooltipContainerStyles.left(showDirectionalArrow) : tooltipSettings.x + "px", right: directionToOpen ? tooltipContainerStyles.right(showDirectionalArrow) : null, showDirectionalArrow: directionToOpen && showDirectionalArrow, tooltipArrowBoxShadowSizing: tooltipArrowStyles.boxShadowSizing, tooltipArrowTop: tooltipArrowStyles.top, tooltipArrowLeft: tooltipArrowStyles.left },
                React.createElement(SuspenseWrapper, null, tooltipSettings.content))), document.body));
};
//# sourceMappingURL=Tooltip.js.map